import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, XAxis, YAxis, Tooltip, Cell, LabelList } from 'recharts';
import { useTheme } from 'styled-components';
import AgreementBlock from './AgreementBlock/AgreementBlock';
import ChangeBlock from './ChangeBlock/ChangeBlock';
import CustomBarShape from './CustomBarLine/CustomBarLine';
import { TooltipCont } from './TopicLevelChart.styles';
import XAxisLabel from './XAxisLabel/XAxisLabel';
import XAxisLabelBottom from './XAxisLabelBottom/XAxisLabelBottom';
import { ICategoryResult } from '../../../services/reports';

interface TopicLevelChartProps {
  data: ICategoryResult[];
  togglersState: { historical_result: boolean; agreement: boolean };
}

interface TooltipData {
  x: number;
  y: number;
  date: string;
  visible: boolean;
}

interface ICustomBarShapeRechartsProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: {
    historicalScore?: number | null;
    surveyDate?: string | null;
  };
}

const TopicLevelChart: FC<TopicLevelChartProps> = ({ data, togglersState }) => {
  const [tooltip, setTooltip] = useState<TooltipData>({ x: 0, y: 0, date: '', visible: false });

  const isAgreementActive = togglersState.agreement;

  const handleHover = (x: number, y: number, date: string) => {
    setTooltip({ x, y, date, visible: true });
  };

  const handleLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const theme = useTheme();
  const { t } = useTranslation();

  const flatData = data.flatMap((category, categoryIndex) =>
    category.topicResults.map((topicResult, topicIndex) => ({
      ...topicResult,
      categoryIndex,
      topicIndex,
      fullIndex: categoryIndex * 100 + topicIndex,
      change: topicResult.benchmarks?.historical?.change || 0,
      historicalScore: topicResult.benchmarks?.historical?.score || null,
      surveyDate: topicResult.benchmarks?.survey?.endDate || null,
    }))
  );

  const chartWidth = 1080;
  const chartHeight = 600 + 65;
  const topMargin = 40;
  const bottomMargin = 100 + 65;
  const leftPadding = 65 + 10;
  const barSpacing = 12;
  const barWidth = 24;

  const totalBars = flatData.length;
  const totalCategories = data.length;
  const totalBarWidth = totalBars * (barWidth + 1);
  const totalBarSpacing = (totalBars - totalCategories) * barSpacing;
  const availableWidthForGroups = chartWidth - leftPadding * 2 - totalBarWidth - totalBarSpacing;
  const dynamicGroupSpacing = availableWidthForGroups / (totalCategories - 1);
  const maxBarHeight = chartHeight - topMargin - bottomMargin;
  const reversedCategories = totalCategories * 100 - 100;

  let currentX = leftPadding;
  const positionedData = flatData.map((item, index) => {
    if (index > 0 && item.categoryIndex !== flatData[index - 1].categoryIndex) {
      currentX += dynamicGroupSpacing;
    }
    const xPosition = currentX;
    currentX += barWidth + barSpacing;

    return { ...item, xPosition };
  });

  return (
    <BarChart
      width={chartWidth}
      height={chartHeight}
      margin={{ top: topMargin, bottom: bottomMargin }}
      data={positionedData}
    >
      <XAxis
        axisLine={false}
        tickLine={false}
        xAxisId="0"
        orientation="top"
        tick={props => <XAxisLabel {...props} data={data} width={1080} />}
        ticks={[1]}
      />
      <XAxis
        axisLine={false}
        tickLine={false}
        xAxisId="bottom"
        orientation="bottom"
        tick={props => (
          <XAxisLabelBottom
            {...props}
            data={data}
            positionedData={positionedData}
            width={1080}
            isAgreementActive={isAgreementActive}
          />
        )}
        ticks={[1]}
      />
      <YAxis
        yAxisId="left"
        axisLine={false}
        domain={[0, 100]}
        fontSize={14}
        fontWeight={400}
        padding={{ top: 12, bottom: 12 }}
        tickLine={false}
        tickCount={11}
        tickSize={24}
        interval={0}
      />

      <Tooltip
        content={() => {
          if (!tooltip.date) {
            return <TooltipCont>No Date Available</TooltipCont>;
          }

          try {
            const formattedDate = new Intl.DateTimeFormat('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }).format(new Date(tooltip.date));

            return <TooltipCont>{formattedDate}</TooltipCont>;
          } catch (error) {
            return <TooltipCont>Invalid Date</TooltipCont>;
          }
        }}
        active={tooltip.visible}
        cursor={false}
        isAnimationActive={false}
      />

      <Bar
        dataKey={() => 100}
        yAxisId="left"
        fill={theme.colors.neutral.primary[10]}
        barSize={barWidth}
        radius={[4, 4, 4, 4]}
      >
        {positionedData.map((entry, index) => (
          <Cell key={`bg-cell-${index}`} x={entry.xPosition} />
        ))}
      </Bar>

      <Bar
        dataKey="score"
        yAxisId="left"
        fill={theme.colors.primary[10]}
        barSize={barWidth}
        shape={(props: ICustomBarShapeRechartsProps) => (
          <CustomBarShape
            x={props.x ?? 0}
            y={props.y ?? 0}
            width={props.width ?? 0}
            height={props.height ?? 0}
            fill={props.fill ?? theme.colors.primary[10]}
            historicalScore={props.payload?.historicalScore ?? null}
            surveyDate={props.payload?.surveyDate ?? null}
            togglersState={togglersState}
            maxBarHeight={maxBarHeight}
            onHover={handleHover}
            onLeave={handleLeave}
          />
        )}
      >
        {positionedData.map((entry, index) => {
          let fillColor;
          switch (entry.scoreRating) {
            case 'weak':
              fillColor = theme.colors.accent.red[70];
              break;
            case 'strong':
              fillColor = theme.colors.accent.green[70];
              break;
            case 'normal':
            default:
              fillColor = theme.colors.accent.blue[70];
              break;
          }

          return <Cell key={`cell-${index}`} x={entry.xPosition} fill={fillColor} />;
        })}
        <LabelList
          dataKey="score"
          position="insideTop"
          fill="#FFFFFF"
          fontSize={12}
          fontWeight="bold"
        />
      </Bar>

      <svg>
        <text
          x={leftPadding - 70}
          y={485}
          fill={theme.colors.neutral.primary[50]}
          fontSize={12}
          fontWeight="bold"
          textAnchor="start"
        >
          {t('report.effect.table.change')}
        </text>
        {positionedData.map((entry, index) => {
          let bgColor;
          let textColor;
          if (entry.fullIndex >= reversedCategories) {
            if (entry.change === 0) {
              bgColor = theme.colors.accent.blue[30];
              textColor = theme.colors.accent.blue[100];
            } else if (entry.change < 0) {
              bgColor = theme.colors.accent.green[30];
              textColor = theme.colors.accent.green[100];
            } else {
              bgColor = theme.colors.accent.red[30];
              textColor = theme.colors.accent.red[100];
            }
          } else {
            if (entry.change === 0) {
              bgColor = theme.colors.accent.blue[30];
              textColor = theme.colors.accent.blue[100];
            } else if (entry.change < 0) {
              bgColor = theme.colors.accent.red[30];
              textColor = theme.colors.accent.red[100];
            } else {
              bgColor = theme.colors.accent.green[30];
              textColor = theme.colors.accent.green[100];
            }
          }

          return (
            <ChangeBlock
              key={`change-block-${index}`}
              x={entry.xPosition}
              y={470}
              width={barWidth}
              height={22}
              value={entry.change}
              bgColor={bgColor}
              textColor={textColor}
            />
          );
        })}
      </svg>

      {isAgreementActive && (
        <svg>
          <text
            x={leftPadding - 70}
            y={515}
            fill={theme.colors.neutral.primary[50]}
            fontSize={12}
            fontWeight="bold"
            textAnchor="start"
          >
            {t('report.effect.timestamps.agreements')}
          </text>
          {positionedData.map((entry, index) => {
            return (
              <AgreementBlock
                key={`change-block-${index}`}
                x={entry.xPosition}
                y={500}
                width={barWidth}
                height={22}
                value={entry.agreementLevel}
                bgColor={theme.colors.neutral.primary[10]}
              />
            );
          })}
        </svg>
      )}
    </BarChart>
  );
};

export default TopicLevelChart;
